import { useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBoard } from "@/hooks/useBoard";
import emailData from "@/data/emailBoard.json";
import mailData from "@/data/mailBoard.json";
import mailFeedsData from "@/data/mailFeeds.json";
import { EmailBoard } from "./EmailBoard";
import { MailClient } from "./MailClient";
import { Card } from "@/design/primitives";
import { AuthGate } from "@/screens/cc";


// brand key → { slug, name } for the three data sets
const BRAND_SCOPE_MAP = {
  tjb: { slug: "truejoybirthing", name: "True Joy Birthing" },
  at: { slug: "agentictrust", name: "AgenticTrust" },
  to: { slug: "trustoffice", name: "TrustOffice" },
  av: { slug: "aeriusview", name: "AeriusView" },
  wp: { slug: "wingpoint", name: "WingPoint" },
  tm: { slug: "trustminutes", name: "TrustMinutes" },
  sv: { slug: "socializevideo", name: "SocializeVideo" },
  sd: { slug: "stenodesk", name: "StenoDesk" },
};

export default function EmailBoardPage({ brandScope }) {
  const desktop = useMediaQuery("(min-width: 1024px)");
  // Live arrival: poll while visible so IDLE-watcher ingests show up without
  // a manual refresh (spec Piece 3 — the UI leg; ingest itself is event-driven).
  const mail = useBoard("mail", { pollMs: 30000 });
  const wall = useBoard("email", { pollMs: 30000 });
  const feeds = useBoard("mailfeeds", { pollMs: 30000 });
  const [dismissed, setDismissed] = useState(false);

  const scopeInfo = brandScope ? BRAND_SCOPE_MAP[brandScope] : null;

  // Live mailbox account feeds (script-only IMAP peek; google accounts incl.) — fall
  // back to the baked window when the feed board is unreachable. Scoped: brand rows only.
  const bakedFeedAccounts = useMemo(() => {
    const accounts = (mailFeedsData.accounts || []).map((a) => ({
      slug: `mfeed:${a.brand}:${a.mailbox}`,
      kind: "mail-feed-account",
      state: (a.unseen || 0) > 0 ? "attention" : "waiting",
      payload: {
        title: a.label, mailbox: a.mailbox, brand: a.brand, google: a.google,
        total: a.total, unseen: a.unseen, error: a.error,
        latest: (a.messages || []).slice(0, 12).map((m) => ({
          from: m.from, subject: m.subject, date: m.date, seen: m.seen,
        })),
      },
    }));
    if (!scopeInfo) return accounts;
    return accounts.filter((a) => a.payload.brand === scopeInfo.slug);
  }, [scopeInfo]);

  const liveFeedItems = useMemo(() => feeds.items || [], [feeds.items]);
  const viewFeedItems = useMemo(() => {
    const raw = feeds.live && liveFeedItems.length ? liveFeedItems : bakedFeedAccounts;
    if (!scopeInfo) return raw;
    return raw.filter((i) => i.payload?.brand === scopeInfo.slug);
  }, [feeds.live, liveFeedItems, bakedFeedAccounts, scopeInfo]);
  const viewFeedMeta = feeds.live && feeds.meta ? feeds.meta.source_stamp : mailFeedsData.stamp;

  const liveMailItems = useMemo(() => mail.items || [], [mail.items]);
  const bakedMailItems = useMemo(
    () => (mailData.cards || []).map((c) => ({ ...c, payload: c.payload || {} })),
    [],
  );
  const viewMailItemsRaw = mail.live && liveMailItems.length ? liveMailItems : bakedMailItems;
  const viewMailMeta = mail.live && mail.meta ? mail.meta : mailData.stamp;

  // Scope items when brandScope is set
  const viewMailItems = useMemo(() => {
    if (!scopeInfo) return viewMailItemsRaw;
    // For mail, items carry brand at payload.brand (slug form)
    return viewMailItemsRaw.filter((i) => i.payload?.brand === scopeInfo.slug);
  }, [viewMailItemsRaw, scopeInfo]);

  const liveWallItems = useMemo(() => wall.items || [], [wall.items]);
  const bakedWallItems = useMemo(
    () => (emailData.cards || []).map((c) => ({ ...c, payload: c.payload || {} })),
    [],
  );
  const viewWallItemsRaw = wall.live && liveWallItems.length ? liveWallItems : bakedWallItems;
  const viewWallItems = useMemo(() => {
    if (!scopeInfo) return viewWallItemsRaw;
    // For wall, items carry brand at payload.brand (slug form)
    return viewWallItemsRaw.filter((i) => i.payload?.brand === scopeInfo.slug);
  }, [viewWallItemsRaw, scopeInfo]);

  const viewWallMeta = useMemo(() => {
    if (wall.live && wall.meta?.clean_total != null) {
      return {
        ingested_at: wall.meta.ingested_at,
        as_of: wall.meta.as_of,
        hours: wall.meta.hours,
        fresh: wall.meta.fresh,
        dead: wall.meta.dead,
        clean_total: wall.meta.clean_total,
        waiting_longer_total: wall.meta.waiting_longer_total,
        old_total: wall.meta.old_total,
      };
    }
    // Baked fallback: aggregate concrete cards only, never rollup/watcher cards.
    const concrete = viewWallItems.filter((i) => !i.slug?.startsWith("email:reply-lag") && i.kind !== "watcher");
    return {
      ...emailData.stamp,
      clean_total: concrete.reduce((s, i) => s + (i.payload?.clean || 0), 0),
      waiting_longer_total: concrete.reduce((s, i) => s + (i.payload?.waitingLonger || 0), 0),
      old_total: concrete.reduce((s, i) => s + (i.payload?.old || 0), 0),
    };
  }, [wall.live, wall.meta, viewWallItems]);

  // Signed-out is read-only in both views; the client shows the committed window.
  if (mail.status === "needs-signin") {
    return (
      <div data-testid="email-page" className="w-full font-inter text-os-ink">
        <div className={desktop ? "mx-auto flex w-full max-w-[140rem] flex-col gap-3 px-5 py-4" : "flex flex-col gap-3 px-3 pb-6 pt-3"}>
          <AuthGate />
          <MailClient
            items={scopeInfo ? viewMailItems : bakedMailItems}
            meta={mailData.stamp}
            board={mail}
            variant={desktop ? "desktop" : "mobile"}
            onNotice={() => {}}
            brandScope={scopeInfo}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="email-page" className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex w-full max-w-[140rem] flex-col gap-3 px-5 py-4" : "flex flex-col gap-3 px-3 pb-6 pt-3"}>
        {!mail.live && (
          <Card quiet className="px-5 py-3">
            <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
              Kit's mail feed is offline this visit — the page shows the last committed window.
            </p>
          </Card>
        )}
        {mail.live && !dismissed && !mail.meta?.ingested_at && (
          <button
            type="button"
            data-testid="mail-sample-note"
            onClick={() => setDismissed(true)}
            className="self-start rounded-control border border-os-border px-3 py-1.5 text-left font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink"
          >
            Today this window is a hand-built sample — Kit's live mail sync arrives on the first fetch. Tap to dismiss.
          </button>
        )}
        <MailClient
          items={viewMailItems}
          meta={viewMailMeta}
          feedItems={viewFeedItems}
          feedStamp={viewFeedMeta}
          board={mail}
          variant={desktop ? "desktop" : "mobile"}
          onNotice={mail.setNotice}
          brandScope={scopeInfo}
        />
        {mail.notice?.text && (
          <p className="px-1 text-[0.8125rem] text-os-amber" data-testid="mail-notice">{mail.notice.text}</p>
        )}
        {!scopeInfo && (
          <details className="mx-1 border-t border-os-border pt-3" data-testid="mail-wall-details">
            <summary className="cursor-pointer font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-3 hover:text-os-ink">
              Zero-inbox wall — the per-brand counts view
            </summary>
            <div className="pt-3">
              <EmailBoard items={viewWallItems} meta={viewWallMeta} variant={desktop ? "desktop" : "mobile"} board={wall} />
              {!wall.live && (
                <Card quiet className="px-5 py-3">
                  <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
                    Kit's mail counts are offline this visit — the page shows the last committed state.
                  </p>
                </Card>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
